<template>
  <div>
    <div class="bg-blue-900 p-4 lg:p-12">
      <div class="max-w-5xl mx-auto mb-12">
        <div class="flex flex-col md:flex-row md:items-center">
          <h4 class="text-white font-bold">Subscriptions</h4>

          <div class="flex md:ml-auto mt-4 md:mt-0">
            <BaseButton
              v-if="$membershipHasPermissionTo('create account subscriptions')"
              tag="router-link"
              :to="{ name: 'accounts.accountSubscriptions.create', params: { accountId } }"
              primary
              class="ml-auto md:ml-4"
            >
              New Subscription
            </BaseButton>
          </div>
        </div>
      </div>
    </div>
    <div class="px-4 pb-4 lg:px-12 lg:pb-12 -mt-12">
      <div class="max-w-5xl mx-auto">
        <div class="space-y-16">
          <VList :meta="meta">
            <template #before>
              <div class="hidden lg:flex font-medium text-sm text-gray-600 mb-4 pr-2">
                <div class="flex-1 px-4">Name</div>
                <div class="w-48 px-4">Date Start</div>
                <div class="w-48 px-4 mr-12 text-right">Date Created</div>
              </div>
            </template>

            <template v-if="isLoading" #default>
              <div class="odd:bg-gray-100 border border-gray-200 rounded p-4 text-center">
                <Loader />
              </div>
            </template>

            <template v-else-if="isRejected" #default>
              <div
                class="odd:bg-red-100 border border-red-200 rounded p-4 text-red-600 text-center"
              >
                Something went wrong. Please try again later.
              </div>
            </template>

            <template v-else-if="accountSubscriptions.length" #default>
              <template v-for="accountSubscription in accountSubscriptions">
                <VListLine
                  :key="`accountSubscriptions_${accountSubscription.uuid}`"
                  :account-subscription="accountSubscription"
                  :disabled="isLoading"
                  @account-subscription:view="viewSubscription"
                />
              </template>
            </template>

            <template v-else #default>
              <div class="odd:bg-gray-100 border border-gray-200 rounded p-4 text-center">
                No results found
              </div>
            </template>
          </VList>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import { middleware } from '@/routes/guards';
import store from '@/store';

import VList from '@/components/Common/VList.vue';
import VListLine from '@/components/AccountSubscriptions/VAccountListLine.vue';

const STATUS_IDLE = 'idle';
const STATUS_PENDING = 'pending';
const STATUS_RESOLVED = 'resolved';
const STATUS_REJECTED = 'rejected';

export default {
  components: {
    VList,
    VListLine,
  },
  props: {
    accountId: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    status: STATUS_IDLE,
  }),
  computed: {
    ...mapState('ui/accountAccountSubscriptions', ['meta']),
    ...mapGetters('ui/accountAccountSubscriptions', ['accountSubscriptions']),
    isLoading() {
      return this.status === STATUS_PENDING;
    },
    isResolved() {
      return this.status === STATUS_RESOLVED;
    },
    isRejected() {
      return this.status === STATUS_REJECTED;
    },
  },
  watch: {
    $route: 'fetchData',
  },
  destroyed() {
    this.setIds([]);
    this.setMeta(null);
    this.setLinks(null);
  },
  beforeRouteEnter(to, from, next) {
    const check = middleware('account-permission:read account subscriptions');

    check(to, from, async (args) => {
      if (typeof args !== 'undefined') {
        return next(...args);
      }

      const { accountId } = to.params;
      const { query: params } = to;

      try {
        await store.dispatch('ui/accountAccountSubscriptions/load', { accountId, params });
        return next();
      } catch (error) {
        return next(false);
      }
    });
  },
  methods: {
    ...mapActions('ui/accountAccountSubscriptions', {
      loadAccountSubscriptions: 'load',
    }),
    ...mapMutations('ui/accountAccountSubscriptions', ['setIds', 'setMeta', 'setLinks']),
    async fetchData() {
      const { accountId } = this;
      const { query: params } = this.$route;
      this.status = STATUS_PENDING;
      try {
        await this.loadAccountSubscriptions({ accountId, params });
        this.status = STATUS_RESOLVED;
      } catch (error) {
        this.status = STATUS_REJECTED;
      }
    },
    viewSubscription(subscription) {
      if (!subscription.can.view) return;

      this.$router.push({
        name: 'accounts.accountSubscriptions.accountSubscriptionUuid',
        params: {
          accountId: this.accountId,
          accountSubscriptionUuid: subscription.uuid,
        },
      });
    },
  },
};
</script>
